import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { catColor, CategoryIcon } from "@/lib/categories";

export default function CategoryCombobox({ categories = [], value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const trimmed = query.trim();
  const exists = categories.some((c) => c.toLowerCase() === trimmed.toLowerCase());

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          data-testid="category-trigger"
          className="h-12 w-full justify-between rounded-xl font-normal"
        >
          <span className="flex items-center gap-2 truncate">
            {value && (
              <span
                className="grid h-6 w-6 place-items-center rounded-md"
                style={{ background: catColor(value) }}
              >
                <CategoryIcon category={value} size={14} color="#fff" />
              </span>
            )}
            <span className={cn("truncate", !value && "text-slate-400")}>
              {value || "Kategorie wählen"}
            </span>
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
      >
        <Command>
          <CommandInput
            placeholder="Suchen oder eingeben…"
            value={query}
            onValueChange={setQuery}
            data-testid="category-search"
          />
          <CommandList>
            <CommandEmpty>Keine Kategorie gefunden</CommandEmpty>
            {trimmed && !exists && (
              <CommandGroup heading="Benutzerdefiniert">
                <CommandItem
                  value={`__custom__${trimmed}`}
                  onSelect={() => {
                    onChange(trimmed);
                    setOpen(false);
                  }}
                  data-testid="category-custom"
                >
                  <span className="mr-2 text-primary font-semibold">+</span>
                  „{trimmed}" verwenden
                </CommandItem>
              </CommandGroup>
            )}
            <CommandGroup heading="Kategorien">
              {categories.map((c) => (
                <CommandItem
                  key={c}
                  value={c}
                  onSelect={() => {
                    onChange(c);
                    setOpen(false);
                  }}
                >
                  <span
                    className="mr-2 grid h-6 w-6 place-items-center rounded-md"
                    style={{ background: catColor(c) }}
                  >
                    <CategoryIcon category={c} size={13} color="#fff" />
                  </span>
                  <span className="flex-1 truncate">{c}</span>
                  <Check
                    className={cn("ml-2 h-4 w-4", value === c ? "opacity-100" : "opacity-0")}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
